import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  CircleDot,
  FileText,
  RefreshCw,
  Send
} from "lucide-react";
import { useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { ListCard } from "@/components/ui/list-card";
import { MetaLine } from "@/components/ui/meta-line";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import type { Artifact } from "../../../caseIde/types";
import type {
  BriefRef,
  EvidenceRow,
  IssueScore,
  NoticeIssueBrief,
  OurPosition,
  UscisVerdict
} from "../../types";

const VERDICT: Record<UscisVerdict, { label: string; tone: StatusTone }> = {
  not_fulfilled: { label: "Not Met", tone: "danger" },
  partially_fulfilled: { label: "Partly Met", tone: "warn" },
  procedural: { label: "Procedural", tone: "neutral" },
  raised: { label: "Raised", tone: "warn" }
};

const POSITION: Record<OurPosition, { label: string; tone: StatusTone }> = {
  pending: { label: "Analyzing", tone: "neutral" },
  ready: { label: "Ready", tone: "success" },
  needs_evidence: { label: "Needs Evidence", tone: "warn" },
  procedural: { label: "Short Answer", tone: "info" }
};

const SCORE: Record<IssueScore["status"], { label: string; tone: StatusTone }> =
  {
    answered: { label: "Answered", tone: "success" },
    needs_evidence: { label: "Answered", tone: "success" },
    needs_work: { label: "Needs Work", tone: "warn" },
    missing: { label: "Not Answered", tone: "danger" }
  };

const ROW_ICON = {
  met: CheckCircle2,
  partial: CircleDot,
  missing: CircleDashed
} as const;

const ROW_TONE = {
  met: "text-success",
  partial: "text-warn",
  missing: "text-danger"
} as const;

const ROW_STATUS = {
  met: "Met",
  partial: "Partly met",
  missing: "Missing"
} as const;

/** Past this, the quote opens with Show Full Quote. */
const LONG_QUOTE = 180;

const ordinal = (n: number): string => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
};

/** What the client should send for an issue: its gaps, or what USCIS asked for. */
export const issueGaps = (issue: NoticeIssueBrief): string[] => {
  const gaps = issue.evidence.flatMap((row) =>
    row.status === "met" ? [] : row.gaps.length ? row.gaps : [row.requirement]
  );
  if (gaps.length) return [...new Set(gaps)];
  return issue.our_position === "pending" ? issue.requested : [];
};

export type IssueCardProps = {
  issue: NoticeIssueBrief;
  /** "RFE", "NOID" or "NOIR". */
  label: string;
  noticeNodeId: string;
  memoDraftId?: string;
  /** The response draft, once it has content. */
  responseDraftId?: string;
  /** How the response draft answers this issue, once it is delivered. */
  score?: IssueScore;
  /** Position in the strategy's argument order, 1-based. */
  argueAt?: number;
  rerunning?: boolean;
  onOpen: (artifact: Artifact) => void;
  onRerun: (key: string) => void;
  onRequest?: (issue: NoticeIssueBrief, gaps: string[]) => void;
  onReveal?: (draftId: string, text: string) => void;
};

const RefTag: FC<{ ref_: BriefRef; onOpen: (a: Artifact) => void }> = ({
  ref_,
  onOpen
}) => {
  const name = `${ref_.title || "Exhibit"}${
    ref_.page ? ` p. ${ref_.page}` : ""
  }`;
  return (
    <button
      type="button"
      title={name}
      onClick={() =>
        onOpen({
          kind: "file",
          id: ref_.node_id,
          title: ref_.title || "Exhibit",
          page: ref_.page ?? null
        })
      }
      className="max-w-full min-w-0 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
    >
      <Tag className="hover:bg-fill-3">
        <FileText aria-hidden />
        <span className="min-w-0 truncate">{name}</span>
      </Tag>
    </button>
  );
};

const COLUMNS =
  "grid grid-cols-1 gap-x-3.5 gap-y-1.5 @lg/brief:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]";

const ColumnHead: FC<{ children: string }> = ({ children }) => (
  <span className="hidden text-sm text-fg-3 @lg/brief:block">{children}</span>
);

/** The column's name over its cell where the columns stack. */
const CellLabel: FC<{ children: string }> = ({ children }) => (
  <span className="text-sm text-fg-3 @lg/brief:hidden">{children}</span>
);

/**
 * Each request USCIS made, across three columns: what it asked for, what
 * was already submitted for it, and what is new or still missing (with
 * Request on a gap). Before the evidence is mapped, the requests alone.
 */
const EvidenceGrid: FC<{
  issue: NoticeIssueBrief;
  onOpen: (a: Artifact) => void;
  onRequest?: (gaps: string[]) => void;
}> = ({ issue, onOpen, onRequest }) => {
  const mapped = issue.evidence.length > 0;
  const rows: EvidenceRow[] = mapped
    ? issue.evidence
    : issue.requested.map((requirement) => ({
        requirement,
        previously_submitted: [],
        new: [],
        gaps: [],
        status: "missing" as const
      }));
  if (!rows.length) return null;
  return (
    <div
      className="@container/brief flex flex-col"
      role="group"
      aria-label="What USCIS Asked For"
    >
      <div className={cn(COLUMNS, "pb-1.5")}>
        <ColumnHead>What USCIS Asked For</ColumnHead>
        <ColumnHead>Already Submitted</ColumnHead>
        <ColumnHead>New or Missing</ColumnHead>
      </div>
      {rows.map((row, i) => {
        const Icon = mapped ? ROW_ICON[row.status] : CircleDashed;
        const gaps = row.status === "met" ? [] : row.gaps;
        return (
          <div key={i} className={cn(COLUMNS, "border-t border-line-1 py-2.5")}>
            <div className="flex min-w-0 items-start gap-2 text-base text-fg">
              <Icon
                className={cn(
                  "mt-0.5 size-3.5 shrink-0",
                  mapped ? ROW_TONE[row.status] : "text-fg-4"
                )}
                aria-label={mapped ? ROW_STATUS[row.status] : "Not mapped yet"}
              />
              <span className="min-w-0 break-words">{row.requirement}</span>
            </div>
            <div
              className="flex min-w-0 flex-wrap content-start items-center gap-1"
              data-testid="evidence-submitted"
            >
              <CellLabel>Already Submitted</CellLabel>
              {row.previously_submitted.length ? (
                row.previously_submitted.map((r) => (
                  <RefTag key={`p-${r.node_id}`} ref_={r} onOpen={onOpen} />
                ))
              ) : (
                <span className="text-sm text-fg-4">
                  {mapped ? "Nothing submitted" : "Not mapped yet"}
                </span>
              )}
            </div>
            <div
              className="flex min-w-0 flex-col items-start gap-1"
              data-testid="evidence-new"
            >
              <CellLabel>New or Missing</CellLabel>
              {row.new.length ? (
                <div className="flex w-full min-w-0 flex-wrap gap-1">
                  {row.new.map((r) => (
                    <RefTag key={`n-${r.node_id}`} ref_={r} onOpen={onOpen} />
                  ))}
                </div>
              ) : null}
              {gaps.map((gap, j) => (
                <span
                  key={j}
                  className="max-w-full text-base break-words text-warn"
                >
                  Missing: {gap}
                </span>
              ))}
              {gaps.length && onRequest ? (
                <Button
                  variant="link"
                  size="xs"
                  onClick={() => onRequest(gaps)}
                >
                  <Send aria-hidden /> Request
                </Button>
              ) : null}
              {!row.new.length && !gaps.length ? (
                <span className="text-sm text-fg-4">
                  {row.status === "met" ? "Nothing needed" : "—"}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** A collapsed part of the card: its title, a trailing state, and its body on open. */
const Disclosure: FC<{
  title: string;
  trailing?: ReactNode;
  testId?: string;
  children: ReactNode;
}> = ({ title, trailing, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col" data-testid={testId}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
      >
        <ChevronDown
          className={cn(
            "size-3.5 text-fg-3 transition-transform",
            !open && "-rotate-90"
          )}
          aria-hidden
        />
        <span className="flex-1 text-base font-medium text-fg-2">{title}</span>
        {trailing}
      </button>
      {open ? <div className="pb-2 pl-5.5">{children}</div> : null}
    </div>
  );
};

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * One issue the notice raises, in the notice's order. The header carries
 * one status, where our answer stands (with Rerun when its analysis
 * failed); USCIS's verdict and the response's score sit on the meta line.
 * Then the quote, what answers each request, and the analysis and the
 * response's notes, collapsed. A ground USCIS asserts against the case
 * gets the Must Rebut treatment.
 */
export const IssueCard: FC<IssueCardProps> = ({
  issue,
  label,
  noticeNodeId,
  memoDraftId,
  responseDraftId,
  score,
  argueAt,
  rerunning,
  onOpen,
  onRerun,
  onRequest,
  onReveal
}) => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const verdict = VERDICT[issue.uscis_verdict];
  const failed =
    issue.our_position === "pending" && issue.analysis.state === "failed";
  const position = failed
    ? { label: "Analysis Failed", tone: "danger" as StatusTone }
    : POSITION[issue.our_position];
  const running =
    issue.analysis.state === "running" || issue.analysis.state === "queued";
  const outdated = issue.analysis.state === "outdated";
  const gaps = issueGaps(issue);
  const rebut = !!issue.derogatory_finding;
  const scored = score ? SCORE[score.status] : null;
  const longQuote = (issue.quote?.length ?? 0) > LONG_QUOTE;
  const canRerun =
    !issue.procedural && !running && issue.analysis.state !== "none";
  const analysisState = running
    ? { text: "Running", tone: "text-fg-3" }
    : failed
    ? { text: "Didn't Finish", tone: "text-danger" }
    : outdated
    ? { text: "Out of Date", tone: "text-warn" }
    : issue.analysis.short_answer
    ? { text: memoDraftId ? "Memo Ready" : "Done", tone: "text-fg-3" }
    : null;
  const findings = score?.findings ?? [];

  return (
    <ListCard
      tone={rebut ? "danger" : "neutral"}
      data-testid={`issue-card-${issue.order}`}
      aria-label={`Issue ${issue.order}: ${issue.title}`}
    >
      <div className="flex flex-col gap-1 p-4">
        <div className="flex items-center gap-2">
          <span className="text-md text-fg-3 tabular-nums">{issue.order}.</span>
          <h4 className="min-w-0 flex-1 truncate text-md font-medium text-fg">
            {issue.title}
          </h4>
          <StatusBadge
            tone={position.tone}
            dot
            aria-label={`Our Position: ${position.label}`}
          >
            {running ? <Spinner className="size-3" /> : null}
            {position.label}
          </StatusBadge>
          {failed ? (
            <Button
              size="xs"
              variant="secondary"
              disabled={rerunning}
              onClick={() => onRerun(issue.key)}
            >
              <RefreshCw
                className={rerunning ? "animate-spin" : undefined}
                aria-hidden
              />
              Rerun
            </Button>
          ) : null}
        </div>
        <MetaLine
          className="flex-wrap"
          data-testid="issue-meta"
          parts={[
            issue.criterion_or_ground,
            `USCIS: ${verdict.label}`,
            scored ? `Response: ${scored.label}` : null,
            issue.regulatory_basis.join(", "),
            argueAt ? `argued ${ordinal(argueAt)}` : null,
            !issue.confirmed ? "not confirmed yet" : null
          ]}
        />
      </div>

      {rebut ? (
        <div className="flex flex-col gap-1 bg-danger-soft px-4 py-3">
          <span className="flex items-center gap-1.5 text-sm font-medium text-danger">
            <AlertTriangle className="size-3.5" aria-hidden />
            Must Rebut: USCIS Asserts
          </span>
          <p className="text-base text-fg">{issue.derogatory_finding}</p>
          <span className="text-sm text-fg-3">
            Rebut it directly; an unanswered finding reads as conceded.
          </span>
        </div>
      ) : null}

      {issue.quote || issue.reasoning ? (
        <div className="flex flex-col gap-1 px-4 py-3">
          <div className="border-l border-line-3 pl-3">
            {issue.quote ? (
              <blockquote
                className={cn(
                  "text-base text-fg-2",
                  !quoteOpen && "line-clamp-2"
                )}
                data-testid="issue-quote"
              >
                “{issue.quote}”
              </blockquote>
            ) : null}
            {issue.reasoning && (quoteOpen || !issue.quote) ? (
              <p className="mt-1 text-base text-fg-3">{issue.reasoning}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3 pl-3">
            {longQuote || (issue.quote && issue.reasoning) ? (
              <Button
                variant="link"
                size="xs"
                className="text-fg-3"
                onClick={() => setQuoteOpen((v) => !v)}
              >
                {quoteOpen ? "Show Less" : "Show Full Quote"}
              </Button>
            ) : null}
            <Button
              variant="link"
              size="xs"
              onClick={() =>
                onOpen({
                  kind: "file",
                  id: noticeNodeId,
                  title: label,
                  page: issue.page ?? null
                })
              }
            >
              <FileText aria-hidden />
              Read in {label}
              {issue.page ? `, p. ${issue.page}` : ""}
            </Button>
          </div>
        </div>
      ) : null}

      {!issue.procedural &&
      (issue.evidence.length || issue.requested.length) ? (
        <div className="px-4 py-3">
          <EvidenceGrid
            issue={issue}
            onOpen={onOpen}
            onRequest={onRequest ? (g) => onRequest(issue, g) : undefined}
          />
        </div>
      ) : null}

      {analysisState || findings.length ? (
        <div className="flex flex-col px-4 py-1.5">
          {analysisState ? (
            <Disclosure
              title="Analysis"
              testId="issue-analysis"
              trailing={
                <span
                  className={cn(
                    "flex items-center gap-1.5 text-sm",
                    analysisState.tone
                  )}
                >
                  {running ? <Spinner className="size-3" /> : null}
                  {analysisState.text}
                </span>
              }
            >
              {running ? (
                <p className="text-base text-fg-3">Researching this issue…</p>
              ) : null}
              {issue.analysis.short_answer ? (
                <p className="text-base text-fg">
                  {issue.analysis.short_answer}
                </p>
              ) : null}
              {outdated ? (
                <p className="mt-1 text-sm text-warn">
                  {issue.new_since_analysis.length
                    ? `Evidence changed since this analysis (${issue.new_since_analysis
                        .map((r) => r.title || r.node_id)
                        .join(", ")}).`
                    : "The issue changed since this analysis."}
                </p>
              ) : null}
            </Disclosure>
          ) : null}
          {findings.length ? (
            <Disclosure
              title={`In the Response (${plural(findings.length, "note")})`}
              testId="issue-response-notes"
            >
              <ul className="flex flex-col gap-1">
                {findings.map((finding, i) => (
                  <li key={i} className="text-base text-fg-2">
                    {finding}
                  </li>
                ))}
              </ul>
            </Disclosure>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2">
        {gaps.length && onRequest ? (
          <Button size="xs" onClick={() => onRequest(issue, gaps)}>
            <Send aria-hidden /> Request From Client
          </Button>
        ) : null}
        {memoDraftId && !running ? (
          <Button
            size="xs"
            variant="ghost"
            onClick={() =>
              onOpen({
                kind: "draft",
                id: memoDraftId,
                title: `${issue.title} Analysis`
              })
            }
          >
            <FileText aria-hidden /> Open Memo
          </Button>
        ) : null}
        {responseDraftId && onReveal ? (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onReveal(responseDraftId, issue.title)}
          >
            {score?.status === "missing" ? "Open Response" : "Show in Response"}
          </Button>
        ) : null}
        {canRerun && !failed ? (
          <Button
            size="xs"
            variant={outdated ? "secondary" : "ghost"}
            disabled={rerunning}
            onClick={() => onRerun(issue.key)}
          >
            <RefreshCw
              className={rerunning ? "animate-spin" : undefined}
              aria-hidden
            />
            Rerun
          </Button>
        ) : null}
      </div>
    </ListCard>
  );
};
